import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
// WebSocketは使用しないため削除: import { useSocket } from '../contexts/SocketContext';
import { useNotification } from '../contexts/NotificationContext';
import './Messages.css';
import { apiService, safeApiCall, api } from '../utils/api';
import config from '../config';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import { AiOutlineSend, AiOutlineSearch, AiOutlinePlus, AiOutlineUser, AiOutlineDelete, AiOutlineArrowLeft } from 'react-icons/ai';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/ja';
import { normalizeImageUrl } from '../components/Post';
import axios from 'axios';
import { onForegroundMessage } from '../firebase';

// moment の設定
moment.locale('ja');
moment.tz.setDefault('Asia/Tokyo');

function Messages() {
  const { currentUser, userRole, checkPermission, isLoggedIn, isAuthenticated, token } = useAuth();
  // WebSocket関連のhooksは削除（SSE主体に移行）
  // const { joinConversation, leaveConversation, on, connected, off, connect, disconnectSocket, isManuallyConnected, setMessagePageActive, isWebSocketMode } = useSocket();
  const { updateMessageCount, fetchUnreadCounts, unreadMessageCount, eventSource, realtimeConnected, stopPolling, startPolling } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  
  // URLクエリパラメータからページ状態を判定
  const searchParams = new URLSearchParams(location.search);
  const pageParam = searchParams.get('page');
  const isMessageDetailPage = !!pageParam; // pageパラメータがあればメッセージ詳細ページ
  
  // スマホ表示用の状態管理（現在は全て単一ページ表示に統一）
  const [isMobile, setIsMobile] = useState(true); // 常にモバイル表示モードに統一
  const [showConversationList, setShowConversationList] = useState(!isMessageDetailPage);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [creatingNew, setCreatingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);
  const messagesEndRef = useRef(null);

  // リアルタイム更新用の状態
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef(null);
  const [lastGlobalUpdate, setLastGlobalUpdate] = useState(null);
  
  // メッセージスマートポーリング用の状態
  const [messagePollingActive, setMessagePollingActive] = useState(false);
  const messagePollingIntervalRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const lastKnownMessageTimestampRef = useRef(null);

  // SSE接続制御用の状態
  const [messageSSEFailureCount, setMessageSSEFailureCount] = useState(0);
  const [useMessagePollingFallback, setUseMessagePollingFallback] = useState(false);
  const [messageSSEConnected, setMessageSSEConnected] = useState(false);
  const messageEventSourceRef = useRef(null);

  // NotificationContextとの同期用状態
  const lastMessageCountSyncRef = useRef(null);
  
  // SSE用のメッセージ処理関数
  const handleNewMessage = (messageData) => {
    if (messageData && messageData.conversation_id === selectedConversation?.id) {
      setMessages(prevMessages => {
        // 重複チェック
        const exists = prevMessages.some(msg => msg.id === messageData.id);
        if (!exists) {
          console.log('📨 [SSE] Adding new message:', messageData);
          return [...prevMessages, messageData];
        }
        return prevMessages;
      });
      setTimeout(forceScrollToBottom, 100);
    }
  };
  
  // 会話一覧の更新
  const refreshConversations = () => {
    console.log('🔄 [SSE] Refreshing conversations');
    fetchConversations(true);
  };
  
  // 初回コンポーネント読み込み時の会話一覧取得
  useEffect(() => {
    console.log('🔄 [INITIAL LOAD] Messages component mounted - fetching conversations');
    fetchConversations(true);
  }, []);
  
  // WebSocket状態制御は削除（SSE主体に移行）
  // useEffect(() => {
  //   const conversationId = selectedConversation?.id;
  //   
  //   if (conversationId) {
  //     console.log(`📱 [MESSAGE DETAIL] Conversation ${conversationId} selected`);
  //   } else {
  //     console.log('📱 [MESSAGE DETAIL] No conversation selected');
  //   }
  // }, [selectedConversation?.id]);
  
  // NotificationContextのunreadMessageCountを監視して会話一覧と同期
  useEffect(() => {
    // 会話一覧ページでのみサイドバーと同期（メッセージ詳細ページでは無効）
    if (!isMessageDetailPage && unreadMessageCount !== lastMessageCountSyncRef.current) {
      console.log('🔄 [CONVERSATION SYNC] NotificationContext triggered conversation list update');
      lastMessageCountSyncRef.current = unreadMessageCount;
      
      // 会話一覧を再取得してバッジを同期
      fetchConversations(true);
    }
  }, [unreadMessageCount, isMessageDetailPage]);

  // メッセージページ専用SSE接続管理（FCMに移行したため無効化）
  useEffect(() => {
    return; // FCMリアルタイム更新に移行
    if (!isAuthenticated || !selectedConversation) {
      // 認証されていない場合またはメッセージが選択されていない場合はSSEを切断
      if (messageEventSourceRef.current) {
        messageEventSourceRef.current.close();
        messageEventSourceRef.current = null;
        setMessageSSEConnected(false);
      }
      return;
    }

    let cleanupFunctions = [];

    // メッセージ専用SSE接続を試行する関数
    const connectMessageSSE = async () => {
      if (useMessagePollingFallback) {
        console.log('📊 [MESSAGE SSE] Using polling fallback mode');
        return null;
      }

      try {
        console.log('🔌 [MESSAGE SSE] Attempting SSE connection for messages...');
        const url = `${config.API_BASE_URL}/api/messages/sse-stream?conversation_id=${selectedConversation.id}`;
        
        // fetch()を使ってSSEストリーミングを処理
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Message SSE connection failed: ${response.status}`);
        }

        console.log('✅ [MESSAGE SSE] Connection established');
        setMessageSSEConnected(true);

        // ReadableStreamでSSEデータを処理
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                console.log('📡 [MESSAGE SSE] Stream ended');
                break;
              }

              // データをデコードしてバッファに追加
              buffer += decoder.decode(value, { stream: true });
              
              // 完全なSSEメッセージを抽出
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // 最後の不完全な行をバッファに保持

              let eventType = '';
              let eventData = '';

              for (const line of lines) {
                if (line.startsWith('event:')) {
                  eventType = line.substring(6).trim();
                } else if (line.startsWith('data:')) {
                  eventData = line.substring(5).trim();
                } else if (line === '' && eventData) {
                  // 完全なイベント処理
                  try {
                    const data = JSON.parse(eventData);
                    console.log('📨 [MESSAGE SSE] Received:', { type: eventType, data });
                    
                    if (data.type === 'new_message') {
                      // 新しいメッセージの処理
                      handleNewMessage(data.data);
                    } else if (data.type === 'conversation_update') {
                      // 会話一覧の更新
                      refreshConversations();
                    }
                  } catch (parseError) {
                    console.error('❌ [MESSAGE SSE] Parse error:', parseError);
                  }
                  
                  // リセット
                  eventType = '';
                  eventData = '';
                }
              }
            }
          } catch (streamError) {
            console.error('❌ [MESSAGE SSE] Stream error:', streamError);
            throw streamError;
          } finally {
            reader.releaseLock();
            setMessageSSEConnected(false);
          }
        };

        // ストリーム処理を開始
        processStream().catch((error) => {
          console.error('❌ [MESSAGE SSE] Processing error:', error);
          setMessageSSEConnected(false);
        });

        // クリーンアップ用の疑似EventSourceオブジェクトを返す
        return {
          close: () => {
            reader.cancel();
            setMessageSSEConnected(false);
          }
        };

      } catch (error) {
        console.error('❌ [MESSAGE SSE] Setup error:', error);
        setMessageSSEConnected(false);
        return null;
      }
    };

    // ポーリングを開始する関数
    const startMessagePolling = () => {
      if (messagePollingIntervalRef.current) return; // 既に動作中の場合は重複を避ける
      
      console.log('📊 [MESSAGE POLLING] Starting message polling...');
      setMessagePollingActive(true);

      messagePollingIntervalRef.current = setInterval(async () => {
        try {
          // 現在の最新メッセージを取得
          const response = await apiService.get(`/api/messages/conversations/${selectedConversation.id}/messages`);
          const serverMessages = response.data.messages || [];
          
          if (serverMessages.length > 0) {
            const latestServerMessage = serverMessages[serverMessages.length - 1];
            const latestLocalMessage = messages[messages.length - 1];
            
            // 新しいメッセージがあるかチェック
            if (!latestLocalMessage || latestServerMessage.id > latestLocalMessage.id) {
              // 新しいメッセージのみを追加
              const newMessages = serverMessages.filter(serverMsg => 
                !messages.some(localMsg => localMsg.id === serverMsg.id)
              );
              
              if (newMessages.length > 0) {
                console.log(`📨 [MESSAGE POLLING] Found ${newMessages.length} new messages`);
                setMessages(prevMessages => [...prevMessages, ...newMessages]);
                setTimeout(forceScrollToBottom, 100);
              }
            }
          }
        } catch (error) {
          console.error('❌ [MESSAGE POLLING] Polling error:', error);
        }
      }, 30000); // 30秒間隔（安定運用していた間隔）
      
      cleanupFunctions.push(() => {
        if (messagePollingIntervalRef.current) {
          clearInterval(messagePollingIntervalRef.current);
          messagePollingIntervalRef.current = null;
          setMessagePollingActive(false);
        }
      });
    };

    // SSE優先で接続を試行
    if (!useMessagePollingFallback) {
      // 非同期処理をuseEffect内で実行
      connectMessageSSE().then((es) => {
        messageEventSourceRef.current = es;
      }).catch((error) => {
        console.error('❌ [MESSAGE SSE] Connection failed:', error);
        setMessageSSEConnected(false);
      });
      
      // SSE接続に失敗した場合の遅延フォールバック
      setTimeout(() => {
        if (!messageSSEConnected && !useMessagePollingFallback) {
          console.log('⏰ [MESSAGE SSE] Connection timeout, starting polling...');
          startMessagePolling();
        }
      }, 5000); // 5秒でタイムアウト
    } else {
      // フォールバックモードの場合は直接ポーリング開始
      startMessagePolling();
    }
    
    // クリーンアップ関数
    return () => {
      if (messageEventSourceRef.current) {
        messageEventSourceRef.current.close();
        messageEventSourceRef.current = null;
      }
      setMessageSSEConnected(false);
      
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [isAuthenticated, selectedConversation, useMessagePollingFallback, token, currentUser, messages]);

  // URLのpageパラメータからメッセージ詳細を表示
  useEffect(() => {
    const handlePageParam = async () => {
      if (pageParam && conversations.length > 0 && !selectedConversation) {
        // pageパラメータに対応する会話を検索
        const targetConversation = conversations.find(conv => {
          // 参加者のユーザーネームでマッチング
          return conv.participants.some(p => p && typeof p === 'object' && p.username === pageParam);
        });
        
        if (targetConversation) {
          console.log(`🔄 [PAGE PARAM] Loading conversation for page: ${pageParam}`);
          await selectConversation(targetConversation);
          } else {
          console.log(`⚠️ [PAGE PARAM] No conversation found for page: ${pageParam}`);
          // 会話が見つからない場合は会話一覧に戻る
          navigate('/messages', { replace: true });
        }
      }
    };
    
    if (conversations.length > 0) {
      handlePageParam();
    }
  }, [pageParam, conversations, selectedConversation]);

  // リアルタイム通信の設定（FCMに移行したため無効化）
  useEffect(() => {
    return; // FCMリアルタイム更新に移行
    const conversationId = selectedConversation?.id;
    
    if (!conversationId) {
      console.log('⏸️ [REALTIME] No conversation selected');
      return;
    }

    // NotificationContextのSSE接続を優先し、失敗時のみポーリング開始
    console.log(`🔍 [REALTIME] SSE Status - connected: ${realtimeConnected}, eventSource: ${!!eventSource}`);
    
    // SSE接続成功時はSSEを使用、EventSourceが存在しない場合のみポーリング
    if (eventSource && realtimeConnected) {
      console.log('✅ [REALTIME] Using NotificationContext SSE for real-time messaging');
      
      // 既存のメッセージポーリングを停止
      if (messagePollingIntervalRef.current) {
        clearInterval(messagePollingIntervalRef.current);
        messagePollingIntervalRef.current = null;
        setMessagePollingActive(false);
        console.log('⏹️ [REALTIME] Stopped message polling - using SSE');
      }
      
      return; // SSE通知でメッセージを受信するため、ポーリングは不要
    } else {
      console.log('⚠️ [REALTIME] SSE unavailable, starting polling fallback after delay');
      
      // SSEが失敗した場合のみポーリング開始（遅延を設けて確実にSSEを優先）
      const pollingTimeout = setTimeout(() => {
        console.log(`🔍 [POLLING DEBUG] Checking conditions - eventSource: ${!!eventSource}, realtimeConnected: ${realtimeConnected}, messagePollingActive: ${messagePollingActive}`);
        
        if ((!eventSource || !realtimeConnected) && !messagePollingActive) {
          console.log('🔄 [REALTIME] Starting lightweight polling fallback');
          startLightweightMessagePolling(selectedConversation);
        } else {
          console.log('✅ [REALTIME] SSE connected during delay or polling already active, skipping');
        }
      }, 5000); // 5秒に短縮してより素早いフォールバック
      
      return () => clearTimeout(pollingTimeout);
    }
  }, [selectedConversation?.id, realtimeConnected, eventSource, currentUser]);
    
  // WebSocket関連のコードは削除（SSE主体に移行）

  // クリーンアップ処理（ポーリング停止のみ）
  useEffect(() => {
    return () => {
      // ポーリングのクリーンアップ
      if (!realtimeConnected) {
        stopLightweightMessagePolling();
      }
    };
  }, [selectedConversation?.id, realtimeConnected]);

  // WebSocket関連は削除（SSE主体でシンプル化）

  // ウィンドウサイズ変更を監視
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowConversationList(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // スマホでのキーボード対応
  useEffect(() => {
    if (!isMobile) return;

    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    if (window.visualViewport) {
      const handleViewportChange = () => {
        const keyboardHeight = window.innerHeight - window.visualViewport.height;
        document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
      };
      
      window.visualViewport.addEventListener('resize', handleViewportChange);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  // スクロールを一番下に移動する関数
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const forceScrollToBottom = () => {
    scrollToBottom();
    setTimeout(scrollToBottom, 100);
    setTimeout(scrollToBottom, 300);
  };

  // 会話一覧を取得する関数（手動実行専用）
  const fetchConversations = async (force = false) => {
    if (!force) {
      console.log('⏸️ [FETCH CONVERSATIONS] Auto-fetch disabled to prevent page reloads - use force=true for manual fetch');
      return;
    }
    
    try {
      setLoading(true);
      const response = await safeApiCall.get('/api/messages/conversations');
      setConversations(response.data.conversations || []);
      console.log('✅ [FETCH CONVERSATIONS] Manual fetch completed');
    } catch (error) {
      console.error('会話リストの取得に失敗しました:', error);
      setError('会話リストの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // メッセージを取得する関数（初回読み込み専用）
  const fetchMessages = async () => {
    console.log('🔄 [FETCH MESSAGES] Function called, checking conditions...');
    console.log('🔄 [FETCH MESSAGES] selectedConversation:', selectedConversation?.id);
    console.log('🔄 [FETCH MESSAGES] messagesLoading:', messagesLoading);
    
    if (!selectedConversation || messagesLoading) {
      console.log('⏸️ [FETCH MESSAGES] Skipping - no conversation or already loading');
      return;
    }
    
    try {
      console.log(`🔄 [FETCH MESSAGES] Initial load for conversation ${selectedConversation.id}`);
      setMessagesLoading(true);
      setError(null);
      
      console.log('🔄 [FETCH MESSAGES] Making API call...');
      const response = await safeApiCall.get(
        `/api/messages/conversations/${selectedConversation.id}/messages`
      );
      
      console.log('🔄 [FETCH MESSAGES] API response received:', response.status);
      const messagesData = response.data.messages || [];
      console.log(`📨 [FETCH MESSAGES] Loaded ${messagesData.length} messages for conversation ${selectedConversation.id}`);
      
      // 未読メッセージの最初のメッセージIDを識別
      const currentUserId = currentUser?.id;
      // 未読ラインの位置を unread_count に基づいて計算
      {
        const conv = conversations.find(c => c.id === selectedConversation?.id);
        const unreadCount = conv?.unread_count || 0;
        if (unreadCount > 0 && unreadCount < messagesData.length) {
          const firstUnreadIndex = messagesData.length - unreadCount;
          setFirstUnreadMessageId(messagesData[firstUnreadIndex]?.id || null);
        } else {
          setFirstUnreadMessageId(null);
        }
      }
      
      // メッセージが空でローカルにデータがある場合は上書きしない
      if (messagesData.length > 0) {
        setMessages(messagesData);
      } else {
        setMessages(prev => prev.length > 0 ? prev : []);
      }
      
      // 最新メッセージのタイムスタンプを記録
      if (messagesData.length > 0) {
        const latestMessage = messagesData[messagesData.length - 1];
        setLastMessageTimestamp(latestMessage.created_at);
        console.log('🔄 [FETCH MESSAGES] Set last message timestamp:', latestMessage.created_at);
      }
      
      // メッセージ読み込み後にスクロール
      setTimeout(() => {
        scrollToBottom();
        console.log('📜 [FETCH MESSAGES] Scrolled to bottom');
      }, 100);

      // 会話を既読にする（ノンブロッキング）- 未読ラインはそのまま維持
      if (selectedConversation) {
        safeApiCall.put(`/api/messages/conversations/${selectedConversation.id}/read`).then(() => {
          if (fetchUnreadCounts) fetchUnreadCounts();
        }).catch(() => {});
      }
      
    } catch (error) {
      console.error('❌ [FETCH MESSAGES] Error:', error);
      console.error('❌ [FETCH MESSAGES] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config,
        url: error.config?.url
      });
      setError('メッセージの取得に失敗しました');
    } finally {
      console.log('🔄 [FETCH MESSAGES] Setting messagesLoading to false');
      setMessagesLoading(false);
    }
  };

  // FCMリアルタイム更新 + フォールバックポーリング（FCM未対応時のみ）
  useEffect(() => {
    if (!selectedConversation) return;

    let pollInterval = null;
    const fcmEnabled = !!localStorage.getItem('fcmToken');

    // FCMフォアグラウンドメッセージでリアルタイム更新
    const unsubscribe = onForegroundMessage((payload) => {
      const data = payload.data || {};
      if (data.type === 'message') {
        if (data.conversation_id === selectedConversation.id) {
          // 新着メッセージ: サーバーから全件取得して置換（重複防止）
          safeApiCall.get(`/api/messages/conversations/${selectedConversation.id}/messages`).then(res => {
            const serverMessages = res.data.messages || [];
            if (serverMessages.length > 0) {
              setMessages(serverMessages);
              setTimeout(forceScrollToBottom, 100);
            }
            safeApiCall.put(`/api/messages/conversations/${selectedConversation.id}/read`).then(() => {
              if (fetchUnreadCounts) fetchUnreadCounts();
            }).catch(() => {});
          }).catch(() => {});
        } else {
          fetchConversations(true);
        }
      }
    });

    // FCMが有効でない場合のみフォールバックポーリング
    if (!fcmEnabled) {
      pollInterval = setInterval(async () => {
        try {
          const res = await safeApiCall.get(`/api/messages/conversations/${selectedConversation.id}/messages`);
          const serverMessages = res.data.messages || [];
          if (serverMessages.length > 0) {
            setMessages(serverMessages);
          }
        } catch (e) {}
      }, 15000);
    }

    return () => {
      unsubscribe();
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [selectedConversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 新しいメッセージのみを取得する関数（定期更新用）- 無効化してDOM操作のみを使用
  const fetchNewMessages = async () => {
    console.log('⏸️ [FETCH NEW] Disabled to prevent page reloads - using pure DOM updates only');
    return 0;
    
    // 以下は無効化
    /*
    if (!selectedConversation || messages.length === 0 || isPolling) return 0;
    setIsPolling(true);
    
    try {
      const realMessages = messages.filter(msg => !msg._isTemporary);
      if (realMessages.length === 0) return 0;
      
      const lastMessageId = realMessages[realMessages.length - 1].id;
      const response = await safeApiCall.get(
        `/api/messages/conversations/${selectedConversation.id}/messages?after=${lastMessageId}`,
        { useCache: false }
      );
      
      const newMessages = response.data.messages || [];
      
      if (newMessages.length > 0) {
        setMessages(prevMessages => {
          const existingIds = new Set(prevMessages.map(msg => msg.id));
          const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
          
          if (uniqueNewMessages.length > 0) {
            const latestMessage = uniqueNewMessages[uniqueNewMessages.length - 1];
            setLastMessageTimestamp(latestMessage.created_at);
            
            setTimeout(() => {
              forceScrollToBottom();
            }, 100);
            
            fetchUnreadCounts(false);
            
            return [...prevMessages, ...uniqueNewMessages];
          }
          
          return prevMessages;
        });
        
        return newMessages.length;
      }
      
      return 0;
    } catch (error) {
      console.error('新しいメッセージの取得エラー:', error);
      return 0;
    } finally {
      setIsPolling(false);
    }
    */
  };

  // 削除されたメッセージをチェックする関数 - 無効化してDOM操作のみを使用
  const checkForDeletedMessages = async () => {
    console.log('⏸️ [CHECK DELETED] Disabled to prevent page reloads - using pure DOM updates only');
    return 0;
    
    // 以下は無効化
    /*
    if (!selectedConversation || messages.length === 0) return 0;
    
    try {
      const currentMessageIds = messages.map(msg => msg.id).filter(id => !id.toString().startsWith('temp_'));
      
      if (currentMessageIds.length === 0) return 0;
      
      const response = await safeApiCall.get(
        `/api/messages/conversations/${selectedConversation.id}/message-ids`,
        { useCache: false }
      );
      
      const serverMessageIds = response.data.message_ids || [];
      const deletedMessageIds = currentMessageIds.filter(id => !serverMessageIds.includes(id));
      
      if (deletedMessageIds.length > 0) {
        setMessages(prevMessages => {
          const filteredMessages = prevMessages.filter(msg => !deletedMessageIds.includes(msg.id));
          
          if (filteredMessages.length !== prevMessages.length) {
            setTimeout(() => {
              const messagesContainer = document.querySelector('.messages__messages');
              if (messagesContainer) {
                const shouldScrollToBottom = messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - 50;
                if (shouldScrollToBottom) {
                  scrollToBottom();
                }
              }
            }, 50);
          }
          
          return filteredMessages;
        });
        
        return deletedMessageIds.length;
      }
      
      return 0;
    } catch (error) {
      if (error.response?.status === 404) {
        return await checkForDeletedMessagesLegacy();
      }
      
      console.error('削除メッセージチェックエラー:', error);
      return 0;
    }
    */
  };

  // レガシー削除チェック（フォールバック用）
  const checkForDeletedMessagesLegacy = async () => {
    if (!selectedConversation || messages.length === 0) return 0;
    
    try {
      // サーバーから最新のメッセージを軽量取得（最新30件のみ）
      const response = await safeApiCall.get(
        `/api/messages/conversations/${selectedConversation.id}/messages?limit=30`
      );
      
      const serverMessages = response.data.messages || [];
      const serverMessageIds = new Set(serverMessages.map(msg => msg.id));
      
      // 現在表示中のメッセージの中で、サーバーに存在しないものを特定
      const currentMessageIds = messages.map(msg => msg.id);
      const deletedMessageIds = currentMessageIds.filter(id => !serverMessageIds.has(id));
      
      // 実際に削除されたメッセージがある場合のみ状態を更新
      if (deletedMessageIds.length > 0) {
        console.log(`🗑️ [DELETED MESSAGES LEGACY] Found ${deletedMessageIds.length} deleted messages`);
        
        // 削除されたメッセージをUIから削除
        setMessages(prevMessages => 
          prevMessages.filter(msg => !deletedMessageIds.includes(msg.id))
        );
        
        return deletedMessageIds.length;
      }
      
      return 0;
    } catch (error) {
      console.error('削除メッセージチェックエラー（レガシー）:', error);
      return 0;
    }
  };

  // スマートポーリング開始（無効化してDOM操作のみを使用）
  const startMessageSmartPolling = (conversationToUse = null) => {
    console.log('⏸️ [SMART POLLING] Disabled to prevent page reloads - using WebSocket + pure DOM only');
    return;
    
    // 以下は無効化
    /*
    const targetConversation = conversationToUse || selectedConversation;
    if (!targetConversation) {
      console.log('⚠️ [MESSAGE SMART POLL] No target conversation for polling');
      return;
    }
    
    stopMessageSmartPolling();
    
    console.log(`🔄 [MESSAGE SMART POLL] Starting smart polling for conversation ${targetConversation.id}`);
    setMessagePollingActive(true);
    
    messagePollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await safeApiCall.get(
          `/api/messages/conversations/${targetConversation.id}/messages`,
          { useCache: false }
        );
        
        const serverMessages = response.data.messages || [];
        const serverMessageCount = serverMessages.length;
        
        if (lastMessageCountRef.current === null) {
          lastMessageCountRef.current = serverMessageCount;
          return;
        }
        
        if (serverMessageCount !== lastMessageCountRef.current) {
          console.log(`📊 [MESSAGE SMART POLL] Message count changed: ${lastMessageCountRef.current} → ${serverMessageCount}`);
          
          setMessages(prevMessages => {
            const existingIds = new Set(prevMessages.filter(msg => !msg._isTemporary).map(msg => msg.id));
            
            let updatedMessages = [...prevMessages];
            
            const deletedMessages = updatedMessages.filter(msg => 
              !msg._isTemporary && !serverMessages.some(serverMsg => serverMsg.id === msg.id)
            );
            
            if (deletedMessages.length > 0) {
              console.log('🗑️ [MESSAGE SMART POLL] Removing deleted messages:', deletedMessages.length);
              updatedMessages = updatedMessages.filter(msg => 
                msg._isTemporary || serverMessages.some(serverMsg => serverMsg.id === msg.id)
              );
            }
            
            const newMessages = serverMessages.filter(serverMsg => !existingIds.has(serverMsg.id));
            
            if (newMessages.length > 0) {
              console.log('📨 [MESSAGE SMART POLL] Adding new messages:', newMessages.length);
              updatedMessages = [...updatedMessages, ...newMessages];
              
              setTimeout(() => {
                const messagesContainer = document.querySelector('.messages__messages');
                if (messagesContainer) {
                  const isNearBottom = messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - 100;
                  
                  if (isNearBottom) {
                    forceScrollToBottom();
                  }
                }
              }, 100);
            }
            
            return updatedMessages;
          });
          
          lastMessageCountRef.current = serverMessageCount;
          
          if (serverMessages.length > 0) {
            const latestMessage = serverMessages[serverMessages.length - 1];
            setLastMessageTimestamp(latestMessage.created_at);
          }
          
          const newMessagesExist = serverMessages.some(msg => 
            !messages.some(existingMsg => existingMsg.id === msg.id)
          );
          
          if (newMessagesExist) {
            fetchConversations();
          }
        }
      } catch (error) {
        console.error('❌ [MESSAGE SMART POLL] Polling error:', error);
      }
    }, 3000);
    */
  };

  // スマートポーリング停止
  const stopMessageSmartPolling = () => {
    if (messagePollingIntervalRef.current) {
      clearInterval(messagePollingIntervalRef.current);
      messagePollingIntervalRef.current = null;
      setMessagePollingActive(false);
      console.log('⏹️ [MESSAGE SMART POLL] Smart polling stopped');
    }
  };

  // 会話選択時のメッセージ読み込み
  useEffect(() => {
    setLastMessageTimestamp(null);
    
    if (selectedConversation) {
      // メッセージを読み込み（FCMリアルタイム更新でポーリング不要）
      fetchMessages();
    }
  }, [selectedConversation]); // 依存配列から関数を削除

  // ページの可視性変更時の処理
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (selectedConversation) {
        // ページが再表示されたら少し遅延してポーリング再開
        setTimeout(startPolling, 500);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedConversation]); // 依存配列から関数を削除

  // 過去メッセージを取得する関数（無限スクロール用）
  // 過去メッセージを取得する関数（全件取得済みのため無効化）
  const fetchOlderMessages = async () => {
    return 0; // 全メッセージは初回読み込みで取得済み
  };

  // 無限スクロール機能
  useEffect(() => {
    if (!selectedConversation) return;
    
    const messagesContainer = document.querySelector('.messages__messages');
    if (!messagesContainer) return;
    
    let isLoading = false;
    
    const handleScroll = async () => {
      if (messagesContainer.scrollTop <= 50 && !isLoading) {
        isLoading = true;
        const loadedCount = await fetchOlderMessages();
        isLoading = false;
        
        if (loadedCount === 0) {
          console.log('📜 これ以上古いメッセージはありません');
        }
      }
    };
    
    messagesContainer.addEventListener('scroll', handleScroll);
    
    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [selectedConversation, messages]);

  // 初回読み込み（手動実行）
  useEffect(() => {
    // 初回のみ手動で会話一覧を取得
    fetchConversations(true);
    
    console.log('🔄 [INIT] Initial manual fetch triggered');
  }, []);

  // メッセージが追加されたら自動スクロール
  useEffect(() => {
    // 動的コンテナをクリア（状態更新時に重複を防ぐ）
    // const dynamicContainer = document.getElementById('messages__dynamic-container');
    // if (dynamicContainer) {
    //   dynamicContainer.innerHTML = '';
    // }
    
    // 状態更新による自動スクロールを無効化（DOM操作で直接制御）
    // forceScrollToBottom();
    
    console.log('📋 [STATE UPDATE] Messages state updated, but not triggering re-render effects');
  }, [messages]);

  // ユーザー検索
  useEffect(() => {
    if (searchQuery.length > 0) {
      const searchUsers = async () => {
        try {
          setSearchLoading(true);
          
          const token = localStorage.getItem('authToken');
          const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
          
          const response = await axios.get(`${config.API_BASE_URL}/api/social/search/users?q=${searchQuery}`, { headers });
          
          const filteredResults = (response.data.users || []).filter(user => 
            currentUser && user.id !== currentUser.id
          );
          
          setSearchResults(filteredResults);
        } catch (error) {
          console.error('ユーザー検索エラー:', error);
        } finally {
          setSearchLoading(false);
        }
      };

      const timeoutId = setTimeout(searchUsers, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, currentUser]);

  // 会話を選択する関数
  const selectConversation = async (conversation) => {
    console.log('💬 [MESSAGES] Selecting conversation:', conversation.id);
    
    // 既存の通知バーを全て削除
    document.querySelectorAll('.messages__new-message-bar').forEach(bar => {
      bar.remove();
      console.log('🗑️ [SELECT] Cleaned up notification bar on conversation select');
    });
    
    // URLをメッセージ詳細ページに変更（参加者のユーザーネームを使用）
    const otherParticipant = conversation.participants.find(p => p && typeof p === 'object' && p.id !== currentUser.id);
    if (otherParticipant) {
      navigate(`/messages?page=${otherParticipant.username}`, { replace: true });
    }
    
    // 前のスマートポーリングを停止
    stopMessageSmartPolling();
    
    // 状態をリセット
    setMessages([]);
    setError(null);
    setCurrentConversationId(conversation.id);
    
    // 選択された会話を設定（fetchMessagesより前に設定）
    setSelectedConversation(conversation);
    
    // 少し待ってから処理を続行（状態更新を確実にする）
    await new Promise(resolve => setTimeout(resolve, 50));

    // 未読カウントをリセット
    if (conversation.unread_count > 0) {
      // 会話一覧の未読カウントを即座に更新（楽観的更新）
      setConversations(prev => prev.map(conv => 
        conv.id === conversation.id 
          ? { ...conv, unread_count: 0 }
          : conv
      ));

      // サーバー側で既読にし、完了後にサイドバーバッジをサーバー値で同期
      safeApiCall.put(`/api/messages/conversations/${conversation.id}/read`)
        .then(() => { if (fetchUnreadCounts) fetchUnreadCounts(); })
        .catch(() => {});
      
      // DOM操作でも即座にバッジを非表示
      const conversationElement = document.querySelector(`[data-conversation-id="${conversation.id}"]`);
      if (conversationElement) {
        const badgeElement = conversationElement.querySelector('.MuiBadge-root');
        if (badgeElement) {
          badgeElement.style.display = 'none';
        }
      }
    }

    // メッセージ取得は useEffect の fetchMessages に任せる
    // モバイルビューでは会話一覧を非表示
    if (isMobile) {
      setShowConversationList(false);
    }
  };

  // スマホ表示で会話一覧に戻る関数
  const handleBackToConversationList = () => {
    stopLightweightMessagePolling();
    setShowConversationList(true);
    setSelectedConversation(null);
    setCreatingNew(false);
    
    // URLを会話一覧ページに変更
    navigate('/messages', { replace: true });
  };

  // 新しい会話を作成する関数
  const handleNewConversation = () => {
    setCreatingNew(true);
    setSelectedConversation(null);
    setSelectedUsers([]);
    setSearchQuery('');
    setSearchResults([]);
    setNewMessage('');
    setError(null);
    
    // PC版では会話一覧を隠さない
    if (isMobile) {
      setShowConversationList(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sendingMessage || !selectedConversation) return false;
    
    const savedMessage = newMessage.trim();
    setSendingMessage(true);
    setNewMessage('');
    
    console.log('📤 [SEND] Sending message:', {
      text: savedMessage,
      conversationId: selectedConversation.id,
      mode: 'HTTP API'
    });
    
    // 送信中は一時的にポーリングを停止（自分のメッセージで新着通知が出るのを防ぐ）
    const wasPollingActive = messagePollingActive;
    if (wasPollingActive) {
      stopLightweightMessagePolling();
    }
    
    try {
      const now = new Date();
      const jstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
      
      // 楽観的メッセージを作成
      const tempMessageId = `temp_${Date.now()}`;
      const tempMessage = {
        id: tempMessageId,
        text: savedMessage,
        created_at: jstNow.toISOString(),
        sender: currentUser,
        _isTemporary: true
      };
      
      // 状態に仮メッセージを追加（Reactで描画）
      setMessages(prevMessages => [...prevMessages, tempMessage]);
      setTimeout(forceScrollToBottom, 50);
      
      // 会話一覧の最新メッセージも即座に更新
      const conversationElement = document.querySelector(`[data-conversation-id="${selectedConversation.id}"] .messages__conversation-last-message`);
      if (conversationElement) {
        conversationElement.textContent = savedMessage;
      }
      
      // バッジがある場合は削除（自分が送信したため）
      const badgeElement = document.querySelector(`[data-conversation-id="${selectedConversation.id}"] .MuiBadge-root`);
      if (badgeElement) {
        badgeElement.style.display = 'none';
      }
      
      // HTTP API経由で送信（シンプル化）
      console.log('📤 [HTTP] Sending via HTTP API');
        
        const messageContent = {
          text: savedMessage,
          created_at: jstNow.toISOString()
        };
        
        const response = await apiService.post(
          `/api/messages/conversations/${selectedConversation.id}/messages`,
          messageContent
        );
        
        if (response.data.message) {
          const realMessage = response.data.message;
          
          console.log('✅ [HTTP] Message sent successfully');
          
          // 状態も更新（仮メッセージを実メッセージに置換）
          setMessages(prevMessages => {
            const filteredMessages = prevMessages.filter(msg => msg.id !== tempMessage.id);
            const normalizedMessage = {
              ...realMessage,
              sender: realMessage.sender || realMessage.user || { id: currentUser.id, username: currentUser.username, display_name: currentUser.display_name, avatar: currentUser.avatar }
            };
            return [...filteredMessages, normalizedMessage];
          });
          
          setLastMessageTimestamp(realMessage.created_at);
          
          // 軽量ポーリング用のタイムスタンプも更新
          lastKnownMessageTimestampRef.current = realMessage.created_at;
          lastMessageCountRef.current = lastMessageCountRef.current + 1;
          
          // 会話一覧の内部状態も更新
          setConversations(prevConversations => {
            return prevConversations.map(conv => {
              if (conv.id === selectedConversation.id) {
                return {
                  ...conv,
                  last_message: realMessage.text || savedMessage,
                  last_message_at: realMessage.created_at,
                  updated_at: realMessage.created_at,
                  unread_count: 0
                };
              }
              return conv;
            });
          });
          
        // 通知処理（SSE通知は自動送信される）
          if (response.data.message.notification_needed && response.data.message.receiver_ids) {
          console.log(`🔔 [HTTP] Message sent - SSE notifications automatically sent`);
        }
      }
      
    } catch (error) {
      console.error('❌ [SEND] Error sending message:', error);
      
      // エラー時は仮メッセージを削除
      setMessages(prevMessages => prevMessages.filter(msg => !msg._isTemporary));
      
      // 会話一覧のプレビューも元に戻す
      const conversationElement = document.querySelector(`[data-conversation-id="${selectedConversation.id}"] .messages__conversation-last-message`);
      if (conversationElement && selectedConversation?.last_message?.text) {
        conversationElement.textContent = selectedConversation.last_message.text;
      }
      
      if (error.response?.status === 500) {
        // 500エラーの場合は楽観的描画を維持（実際には送信されている可能性が高い）
        console.log('🔄 [SEND] 500 error - keeping optimistic message (likely sent)');
        
        const finalMessage = {
          ...tempMessage,
          id: `sent_${Date.now()}`,
          _isTemporary: false
        };
        
        // DOM要素を復活
        if (!pendingElement) {
          const dynamicContainer = document.getElementById('messages__dynamic-container');
          if (dynamicContainer) {
            const messageElement = document.createElement('div');
            messageElement.id = `message_${finalMessage.id}`;
            messageElement.className = 'messages__message messages__message--own';
            
            messageElement.innerHTML = `
              <div class="messages__bubble">
                <p>${savedMessage}</p>
                <span class="messages__timestamp">${moment.utc(finalMessage.created_at).tz('Asia/Tokyo').format('HH:mm')}</span>
              </div>
            `;
            
            dynamicContainer.appendChild(messageElement);
            forceScrollToBottom();
          }
        }
        
        setMessages(prevMessages => [...prevMessages, finalMessage]);
        
      } else if (error.response?.status === 403) {
        alert('このユーザーにメッセージを送ることはできません');
        setNewMessage(savedMessage);
      } else {
        alert('メッセージの送信に失敗しました。再試行してください。');
        setNewMessage(savedMessage);
      }
    } finally {
      setSendingMessage(false);
      
      // ポーリングが有効だった場合は再開
      if (wasPollingActive && selectedConversation) {
        setTimeout(() => {
          console.log('🔄 [SEND] Restarting polling after message send');
          startLightweightMessagePolling(selectedConversation);
        }, 2000);
      }
    }
    
    return false;
  };

  const handleSelectUser = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
      checkBlockStatus(user.id);
    }
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
  };

  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      setSearchLoading(true);
      
      const now = new Date();
      const jstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
      
      const payload = { 
        participant_ids: [selectedUsers[0].id],
        initial_message: newMessage || "こんにちは！",
        created_at: jstNow.toISOString()
      };
      
      const response = await apiService.post('/api/messages/conversations', payload);
      
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setNewMessage('');
      
      const conversation = response.data.conversation;
      if (conversation) {
        const newConv = { 
          id: conversation.id,
          participants: selectedUsers
        };
        setSelectedConversation(newConv);
        setCreatingNew(false);
        
        // 送信したメッセージをローカルに即反映
        if (newMessage) {
          setMessages([{
            id: 'initial_' + Date.now(),
            text: newMessage,
            sender: { id: currentUser.id, username: currentUser.username, display_name: currentUser.display_name, avatar: currentUser.avatar },
            sender_id: currentUser.id,
            created_at: new Date().toISOString(),
            is_read: false,
          }]);
          // fetchMessagesによる上書きを防ぐため、loadingフラグを一時的にセット
          setMessagesLoading(true);
          setTimeout(() => setMessagesLoading(false), 2000);
        }
        
        setConversations(prevConversations => {
          const existingIndex = prevConversations.findIndex(conv => conv.id === conversation.id);
          if (existingIndex >= 0) {
            return prevConversations.map((conv, index) => 
              index === existingIndex ? { ...conv, participants: selectedUsers, last_message: newMessage || '' } : conv
            );
          } else {
            return [{ ...conversation, participants: selectedUsers, last_message: newMessage || '' }, ...prevConversations];
          }
        });
      }
    } catch (error) {
      console.error('会話作成エラー:', error);
      
      if (error.response && error.response.status === 403) {
        alert('このユーザーにメッセージを送ることはできません');
      } else {
        alert('会話の作成に失敗しました。再試行してください。');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // 最後のメッセージの時間をフォーマット
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    const date = moment.utc(dateString).tz('Asia/Tokyo');
    const now = moment().tz('Asia/Tokyo');
    
    const diffDays = now.diff(date, 'days');
    
    if (diffDays === 0) {
      return date.format('HH:mm');
    } else if (diffDays < 7) {
      return date.format('ddd') + '曜日';
    } else {
      return date.format('YYYY/MM/DD');
    }
  };

  // メッセージを日付でグループ化する関数
  const groupMessagesByDate = (messages) => {
    const grouped = [];
    let currentDate = null;
    
    messages.forEach((message, index) => {
      const messageDate = moment.utc(message.created_at).tz('Asia/Tokyo').format('YYYY-MM-DD');
      
      // 日付が変わった場合、日付区切りを追加
      if (currentDate !== messageDate) {
        currentDate = messageDate;
        const dateLabel = moment.utc(message.created_at).tz('Asia/Tokyo');
        const today = moment().tz('Asia/Tokyo').format('YYYY-MM-DD');
        const yesterday = moment().tz('Asia/Tokyo').subtract(1, 'day').format('YYYY-MM-DD');
        
        let displayDate;
        if (messageDate === today) {
          displayDate = '今日';
        } else if (messageDate === yesterday) {
          displayDate = '昨日';
        } else {
          displayDate = dateLabel.format('YYYY年MM月DD日 (ddd)');
        }
        
        grouped.push({
          type: 'date-separator',
          id: `date-${messageDate}`,
          date: displayDate,
          dateValue: messageDate
        });
      }
      
      grouped.push({
        type: 'message',
        ...message
      });
    });
    
    return grouped;
  };

  // メッセージ削除処理
  const handleDeleteMessage = async (messageId) => {
    if (messageId.toString().startsWith('temp_')) {
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      return;
    }
    
    if (!window.confirm('このメッセージを削除してもよろしいですか？削除すると元に戻せません。')) {
      return;
    }
    
    try {
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, _isDeleting: true } 
            : msg
        )
      );
      
      await apiService.delete(`/api/messages/conversations/${selectedConversation.id}/messages/${messageId}`);
      
      // ソフトデリート: メッセージを「削除済み」状態に変更（リストからは除去しない）
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_deleted: true, text: '', _isDeleting: false }
            : msg
        )
      );
      
      // 会話一覧も更新（削除されたメッセージが最新メッセージだった場合）
      setConversations(prevConversations => {
        return prevConversations.map(conv => {
          if (conv.id === selectedConversation.id && conv.last_message) {
            // 削除されたメッセージが最新メッセージだった可能性がある場合
            // 簡単な更新として「メッセージが削除されました」と表示
            return {
              ...conv,
              last_message: {
                ...conv.last_message,
                text: 'メッセージが削除されました'
              }
            };
          }
          return conv;
        });
      });
      
      console.log(`🗑️ [MESSAGE DELETE] Message ${messageId} deleted successfully`);
      
    } catch (error) {
      console.error('メッセージ削除エラー:', error);
      
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, _isDeleting: false } 
            : msg
        )
      );
      
      alert('メッセージの削除に失敗しました。再試行してください。');
    }
  };

  // ユーザーとのブロック状態を確認する関数
  const checkBlockStatus = async (userId) => {
    try {
      await apiService.post('/api/messages/conversations', {
        participant_ids: [userId],
        check_only: true
      });
      
      setIsUserBlocked(false);
      return false;
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setIsUserBlocked(true);
        return true;
      }
      return false;
    }
  };

  // 会話削除処理
  const handleDeleteConversation = async (conversationId, event) => {
    event.stopPropagation();
    
    if (!window.confirm('この会話を削除してもよろしいですか？')) {
      return;
    }
    
    try {
      await apiService.delete(`/api/messages/conversations/${conversationId}`);
      
      setConversations(prevConversations => 
        prevConversations.filter(conv => conv.id !== conversationId)
      );
      
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      
    } catch (error) {
      console.error('会話削除エラー:', error);
      alert('会話の削除に失敗しました。');
    }
  };

  // URLクエリからユーザーIDを取得して会話を開始
  useEffect(() => {
    const handleUserFromQuery = async () => {
      const params = new URLSearchParams(location.search);
      const userIdParam = params.get('user');
      
      if (userIdParam && currentUser) {
        try {
          // userParam は username（文字列）
          if (userIdParam === currentUser.username) return;
          const response = await axios.get(`${config.API_BASE_URL}/api/users/${userIdParam}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            
            const user = response.data;
            if (user && user.id) {
              setSelectedUsers([{
                id: user.id,
                username: user.username,
                display_name: user.display_name || user.displayName || user.username,
                avatar: user.avatar || '',
              }]);
          setCreatingNew(true);
          setSelectedConversation(null);
              
              if (isMobile) {
                setShowConversationList(false);
              }
            }
      } catch (error) {
          console.error('ユーザー情報の取得に失敗しました:', error);
        }
      }
    };

    if (currentUser) {
      handleUserFromQuery();
    }
  }, [location.search, currentUser, isMobile]);

  // メッセージページ専用SSE接続管理（FCMに移行したため無効化）
  useEffect(() => {
    return; // FCMリアルタイム更新に移行
    if (!isAuthenticated || !selectedConversation) {
      // 認証されていない場合またはメッセージが選択されていない場合はSSEを切断
      if (messageEventSourceRef.current) {
        messageEventSourceRef.current.close();
        messageEventSourceRef.current = null;
        setMessageSSEConnected(false);
      }
      return;
    }

    let cleanupFunctions = [];

    // メッセージ専用SSE接続を試行する関数
    const connectMessageSSE = async () => {
      if (useMessagePollingFallback) {
        console.log('📊 [MESSAGE SSE] Using polling fallback mode');
        return null;
      }

      try {
        console.log('🔌 [MESSAGE SSE] Attempting SSE connection for messages...');
        const url = `${config.API_BASE_URL}/api/messages/sse-stream?conversation_id=${selectedConversation.id}`;
        
        // fetch()を使ってSSEストリーミングを処理
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Message SSE connection failed: ${response.status}`);
        }

        console.log('✅ [MESSAGE SSE] Connection established');
        setMessageSSEConnected(true);

        // ReadableStreamでSSEデータを処理
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                console.log('📡 [MESSAGE SSE] Stream ended');
                break;
              }

              // データをデコードしてバッファに追加
              buffer += decoder.decode(value, { stream: true });
              
              // 完全なSSEメッセージを抽出
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // 最後の不完全な行をバッファに保持

              let eventType = '';
              let eventData = '';

              for (const line of lines) {
                if (line.startsWith('event:')) {
                  eventType = line.substring(6).trim();
                } else if (line.startsWith('data:')) {
                  eventData = line.substring(5).trim();
                } else if (line === '' && eventData) {
                  // 完全なイベント処理
                  try {
                    const data = JSON.parse(eventData);
                    console.log('📨 [MESSAGE SSE] Received:', { type: eventType, data });
                    
                    if (data.type === 'new_message') {
                      // 新しいメッセージの処理
                      handleNewMessage(data.data);
                    } else if (data.type === 'conversation_update') {
                      // 会話一覧の更新
                      refreshConversations();
                    }
                  } catch (parseError) {
                    console.error('❌ [MESSAGE SSE] Parse error:', parseError);
                  }
                  
                  // リセット
                  eventType = '';
                  eventData = '';
                }
              }
            }
          } catch (streamError) {
            console.error('❌ [MESSAGE SSE] Stream error:', streamError);
            throw streamError;
          } finally {
            reader.releaseLock();
            setMessageSSEConnected(false);
          }
        };

        // ストリーム処理を開始
        processStream().catch((error) => {
          console.error('❌ [MESSAGE SSE] Processing error:', error);
          setMessageSSEConnected(false);
        });

        // クリーンアップ用の疑似EventSourceオブジェクトを返す
        return {
          close: () => {
            reader.cancel();
            setMessageSSEConnected(false);
          }
        };

      } catch (error) {
        console.error('❌ [MESSAGE SSE] Setup error:', error);
        setMessageSSEConnected(false);
        return null;
      }
    };

    // ポーリングを開始する関数
    const startMessagePolling = () => {
      if (messagePollingIntervalRef.current) return; // 既に動作中の場合は重複を避ける
      
      console.log('📊 [MESSAGE POLLING] Starting message polling...');
      setMessagePollingActive(true);

      messagePollingIntervalRef.current = setInterval(async () => {
        try {
          // 現在の最新メッセージを取得
          const response = await apiService.get(`/api/messages/conversations/${selectedConversation.id}/messages`);
          const serverMessages = response.data.messages || [];
          
          if (serverMessages.length > 0) {
            const latestServerMessage = serverMessages[serverMessages.length - 1];
            const latestLocalMessage = messages[messages.length - 1];
            
            // 新しいメッセージがあるかチェック
            if (!latestLocalMessage || latestServerMessage.id > latestLocalMessage.id) {
              // 新しいメッセージのみを追加
              const newMessages = serverMessages.filter(serverMsg => 
                !messages.some(localMsg => localMsg.id === serverMsg.id)
              );
              
              if (newMessages.length > 0) {
                console.log(`📨 [MESSAGE POLLING] Found ${newMessages.length} new messages`);
                setMessages(prevMessages => [...prevMessages, ...newMessages]);
                setTimeout(forceScrollToBottom, 100);
              }
            }
          }
        } catch (error) {
          console.error('❌ [MESSAGE POLLING] Polling error:', error);
        }
      }, 30000); // 30秒間隔（安定運用していた間隔）
      
      cleanupFunctions.push(() => {
        if (messagePollingIntervalRef.current) {
          clearInterval(messagePollingIntervalRef.current);
          messagePollingIntervalRef.current = null;
          setMessagePollingActive(false);
        }
      });
    };

    // SSE優先で接続を試行
    if (!useMessagePollingFallback) {
      // 非同期処理をuseEffect内で実行
      connectMessageSSE().then((es) => {
        messageEventSourceRef.current = es;
      }).catch((error) => {
        console.error('❌ [MESSAGE SSE] Connection failed:', error);
        setMessageSSEConnected(false);
      });
      
      // SSE接続に失敗した場合の遅延フォールバック
      setTimeout(() => {
        if (!messageSSEConnected && !useMessagePollingFallback) {
          console.log('⏰ [MESSAGE SSE] Connection timeout, starting polling...');
          startMessagePolling();
        }
      }, 5000); // 5秒でタイムアウト
    } else {
      // フォールバックモードの場合は直接ポーリング開始
      startMessagePolling();
    }
    
    // クリーンアップ関数
    return () => {
      if (messageEventSourceRef.current) {
        messageEventSourceRef.current.close();
        messageEventSourceRef.current = null;
      }
      setMessageSSEConnected(false);
      
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [isAuthenticated, selectedConversation, useMessagePollingFallback, token, currentUser, messages]);

  // コンポーネントのマウント・アンマウント時の処理
  useEffect(() => {
    fetchConversations(true); // 初期化時に会話一覧を取得
    
    // デバッグ用のグローバル関数を追加
    window.testMessagePolling = () => {
      console.log('🧪 [DEBUG] Testing message polling...');
      console.log('🧪 [DEBUG] Selected conversation:', selectedConversation?.id);
      console.log('🧪 [DEBUG] Message polling active:', messagePollingActive);
      console.log('🧪 [DEBUG] Polling interval ID:', messagePollingIntervalRef.current);
      console.log('🧪 [DEBUG] Current message count:', messages.length);
      console.log('🧪 [DEBUG] Last message count ref:', lastMessageCountRef.current);
    };
    
    window.forceStartPolling = () => {
      console.log('🧪 [DEBUG] Force starting lightweight polling...');
      console.log('🧪 [DEBUG] Using selectedConversation:', selectedConversation);
      startLightweightMessagePolling(selectedConversation);
    };
    
    window.forceStopPolling = () => {
      console.log('🧪 [DEBUG] Force stopping lightweight polling...');
      stopLightweightMessagePolling();
    };
    
    // ページを離れる時の処理
    const handleBeforeUnload = () => {
      console.log('🚪 [MESSAGES] Page unloading...');
    };

    // ブラウザの閉じる・リロード・ページ遷移を検知
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      console.log('🚪 [MESSAGES] Messages component unmounting...');
      
      // 軽量ポーリングを停止
      stopLightweightMessagePolling();
      
      // 従来のポーリングも停止
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      // デバッグ関数を削除
      delete window.testMessagePolling;
      delete window.forceStartPolling;
      delete window.forceStopPolling;
      
      // イベントリスナーを削除
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 軽量メッセージポーリング（FCMリアルタイム更新に移行したため無効化）
  const startLightweightMessagePolling = (conversationToUse = null) => {
    return; // FCMで即時更新するためポーリング不要
    const targetConversation = conversationToUse || selectedConversation;
    if (!targetConversation) {
      console.log('⚠️ [LIGHTWEIGHT POLL] No target conversation for polling');
      return;
    }
    
    // 既存のポーリングを停止
    stopLightweightMessagePolling();
    
    console.log(`🔄 [LIGHTWEIGHT POLL] Starting new message notification polling for conversation ${targetConversation.id}`);
    setMessagePollingActive(true);
    
    // 最後に確認したメッセージ数とタイムスタンプを記録
    lastMessageCountRef.current = messages.length;
    
    // 現在の最新メッセージのタイムスタンプを記録
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      lastKnownMessageTimestampRef.current = latestMessage.created_at;
      console.log(`🔄 [LIGHTWEIGHT POLL] Initial timestamp set: ${latestMessage.created_at}`);
    } else {
      lastKnownMessageTimestampRef.current = null;
      console.log('🔄 [LIGHTWEIGHT POLL] No messages, timestamp set to null');
    }
    
    messagePollingIntervalRef.current = setInterval(async () => {
      try {
        // 現在のメッセージ数を基準に新着をチェック
        const currentMessageCount = messages.length;
        
        // 全メッセージ数を取得（効率的にカウントのみ）
        const response = await safeApiCall.get(
          `/api/messages/conversations/${targetConversation.id}/messages`,
          { useCache: false }
        );
        
        const serverMessages = response.data.messages || [];
        const serverMessageCount = serverMessages.length;
        
        // 初回設定
        if (lastMessageCountRef.current === null) {
          // DOM要素から現在表示されているメッセージ数を取得
          const displayedMessages = document.querySelectorAll('.messages__message:not(.messages__message--pending)');
          const currentDisplayedCount = displayedMessages.length;
          
          lastMessageCountRef.current = Math.max(serverMessageCount, currentDisplayedCount);
          console.log(`🔄 [LIGHTWEIGHT POLL] Initial setup - Server: ${serverMessageCount}, Displayed: ${currentDisplayedCount}, Set to: ${lastMessageCountRef.current}`);
          return;
        }
        
        // 現在表示中のメッセージIDを正確に取得
        const currentMessageIds = new Set();
        
        // 状態からのメッセージID（空の場合もある）
        messages.forEach(msg => {
          if (msg.id && !isNaN(msg.id)) {
            const parsedId = parseInt(msg.id);
            if (!isNaN(parsedId) && parsedId > 0) {
              currentMessageIds.add(parsedId);
              console.log(`🔍 [STATE ID] Added message ID from state: ${parsedId}`);
            }
          }
        });
        
        // DOM内の実際に表示されているメッセージからIDを取得
        const displayedMessageElements = document.querySelectorAll('.messages__message:not(.messages__message--pending)');
        displayedMessageElements.forEach(el => {
          // data-message-id属性があれば使用、なければid属性から抽出
          const messageId = el.dataset.messageId || el.id?.replace('message_', '');
          if (messageId && !isNaN(messageId) && messageId.trim() !== '') {
            const parsedId = parseInt(messageId);
            if (!isNaN(parsedId) && parsedId > 0) {
              currentMessageIds.add(parsedId);
              console.log(`🔍 [DOM ID] Added displayed message ID: ${parsedId}`);
            }
          }
        });
        
        // DOM内の動的要素からのメッセージID
        const dynamicElements = document.querySelectorAll('[id^="message_"]');
        dynamicElements.forEach(el => {
          const id = el.id.replace('message_', '');
          if (id && !isNaN(id) && id.trim() !== '') {
            const parsedId = parseInt(id);
            if (!isNaN(parsedId) && parsedId > 0) {
              currentMessageIds.add(parsedId);
              console.log(`🔍 [DOM ID] Added dynamic message ID: ${parsedId}`);
            }
          }
        });
        
        console.log(`🔍 [DEBUG] Messages state count: ${messages.length}`);
        console.log(`🔍 [DEBUG] DOM displayed count: ${displayedMessageElements.length}`);
        console.log(`🔍 [DEBUG] Current IDs: [${Array.from(currentMessageIds).join(', ')}]`);
        
        // サーバーの最新メッセージのタイムスタンプを取得
        const serverLatestTimestamp = serverMessages.length > 0 ? 
          serverMessages[serverMessages.length - 1].created_at : null;
        
        console.log(`🔍 [DEBUG] Server latest timestamp: ${serverLatestTimestamp}`);
        console.log(`🔍 [DEBUG] Last known timestamp: ${lastKnownMessageTimestampRef.current}`);
        
        // タイムスタンプベースの新着判定
        let hasNewMessages = false;
        
        if (serverLatestTimestamp && lastKnownMessageTimestampRef.current) {
          // タイムスタンプを比較（新しいメッセージがあるか）
          const serverTime = new Date(serverLatestTimestamp).getTime();
          const lastKnownTime = new Date(lastKnownMessageTimestampRef.current).getTime();
          hasNewMessages = serverTime > lastKnownTime;
        } else if (serverLatestTimestamp && !lastKnownMessageTimestampRef.current) {
          // 初回または空の状態から新着メッセージがある場合
          hasNewMessages = serverMessageCount > lastMessageCountRef.current;
        }
        
        console.log(`📊 [LIGHTWEIGHT POLL] Has new messages: ${hasNewMessages}`);
        
        if (hasNewMessages) {
          // 実際に新しいメッセージのみを特定（タイムスタンプベース）
          let actualNewMessages = [];
          
          if (lastKnownMessageTimestampRef.current) {
            const lastKnownTime = new Date(lastKnownMessageTimestampRef.current).getTime();
            actualNewMessages = serverMessages.filter(msg => {
              const msgTime = new Date(msg.created_at).getTime();
              return msgTime > lastKnownTime && !currentMessageIds.has(msg.id);
            });
          } else {
            // タイムスタンプがない場合は従来のIDベース判定
            actualNewMessages = serverMessages.filter(msg => !currentMessageIds.has(msg.id));
          }
          
          // 自分が送信したメッセージは除外（型を統一して比較）
          const newMessagesFromOthers = actualNewMessages.filter(msg => {
            const msgSenderId = parseInt(msg.sender.id);
            const currentUserId = parseInt(currentUser.id);
            const isFromOthers = msgSenderId !== currentUserId;
            
            console.log(`🔍 [USER ID CHECK] Message ${msg.id}: sender=${msgSenderId}, current=${currentUserId}, fromOthers=${isFromOthers}`);
            return isFromOthers;
          });
          
          console.log(`🔍 [DEBUG] New messages from others: ${newMessagesFromOthers.length}/${actualNewMessages.length}`);
          
          if (newMessagesFromOthers.length > 0) {
            console.log(`📊 [LIGHTWEIGHT POLL] Found ${newMessagesFromOthers.length} new messages from others`);
            
            // メッセージページを開いている間は、バッジを更新せず、新着通知バーのみで表示
            // （バッジの重複カウントを避けるため）
            console.log(`📊 [LIGHTWEIGHT POLL] Message page is open - showing notification bar instead of badge update`);
            
            // 会話一覧の状態は更新しない（バッジカウントの重複を避けるため）
            // setConversations は実行しない
            
            // 入力欄の上に新着通知バーを表示
            const dynamicContainer = document.getElementById('messages__dynamic-container');
            if (dynamicContainer) {
              // 既存の通知バーを削除（全ての場所から）
              document.querySelectorAll('.messages__new-message-bar').forEach(bar => bar.remove());
              
              // 新着通知バーを作成
              const notificationBar = document.createElement('div');
              notificationBar.className = 'messages__new-message-bar';
              notificationBar.style.cssText = `
                position: sticky;
                top: 0;
                z-index: 1000;
                background: #e3f2fd;
                border: 1px solid #1976d2;
                border-radius: 8px;
                padding: 12px;
                margin: 8px 0;
                text-align: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              `;
              
              notificationBar.innerHTML = `
                <div class="messages__new-message-content" style="display: flex; justify-content: center; align-items: center; gap: 12px;">
                  <span class="messages__new-message-text" style="color: #1976d2; font-weight: 500;">${newMessagesFromOthers.length}件の新しいメッセージ</span>
                  <button class="messages__new-message-button" style="
                    background: #1976d2;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    padding: 6px 12px;
                    cursor: pointer;
                    font-size: 14px;
                  ">読み込む</button>
                </div>
              `;
              
              // クリックイベントを追加
              const loadButton = notificationBar.querySelector('.messages__new-message-button');
              loadButton.addEventListener('click', () => {
                loadNewMessages(targetConversation.id);
              });
              
              // 通知バーを最上部に挿入（appendChild ではなく prepend）
              dynamicContainer.insertBefore(notificationBar, dynamicContainer.firstChild);
              
              console.log(`📨 [LIGHTWEIGHT POLL] Displayed notification bar at top for ${newMessagesFromOthers.length} new messages from others`);
            }
          } else if (actualNewMessages.length > 0) {
            // 自分のメッセージのみの場合はログのみ
            console.log(`📝 [LIGHTWEIGHT POLL] Found ${actualNewMessages.length} new messages from self (no notification shown)`);
          } else {
            console.log(`ℹ️ [LIGHTWEIGHT POLL] No actual new messages found`);
          }
          
          // 最新メッセージのタイムスタンプを更新
          if (serverLatestTimestamp) {
            lastKnownMessageTimestampRef.current = serverLatestTimestamp;
            console.log(`🔄 [LIGHTWEIGHT POLL] Updated last known timestamp: ${serverLatestTimestamp}`);
          }
          
          // メッセージ数カウンターも更新
          lastMessageCountRef.current = serverMessageCount;
        } else {
          console.log(`ℹ️ [LIGHTWEIGHT POLL] No new messages (timestamp check)`);
        }
      } catch (error) {
        console.error('❌ [LIGHTWEIGHT POLL] Polling error:', error);
      }
          }, 30000); // 30秒間隔で新着チェック（安定運用していた間隔）
  };



  // 軽量ポーリングを停止する関数
  const stopLightweightMessagePolling = () => {
    if (messagePollingIntervalRef.current) {
      clearInterval(messagePollingIntervalRef.current);
      messagePollingIntervalRef.current = null;
      setMessagePollingActive(false);
      
      // タイムスタンプもリセット
      lastKnownMessageTimestampRef.current = null;
      lastMessageCountRef.current = 0;
      
      console.log('⏹️ [LIGHTWEIGHT POLL] Stopped lightweight message polling and reset timestamps');
    }
        
    // メッセージページを閉じる際に、サイドバーの未読カウントをサーバー値で更新
    if (typeof fetchUnreadCounts === 'function') {
      console.log('🔄 [LIGHTWEIGHT POLL] Updating sidebar message count after closing message page');
      fetchUnreadCounts();
    }
  };

  // 新しいメッセージを読み込む関数
  const loadNewMessages = async (conversationId) => {
    try {
      console.log('🔄 [LOAD NEW] Loading new messages...');
      
      // 最新のメッセージを取得
      const response = await safeApiCall.get(
        `/api/messages/conversations/${conversationId}/messages`,
        { useCache: false }
      );
      
      const serverMessages = response.data.messages || [];
      
      // 現在表示中のメッセージIDを取得（状態 + DOM内の要素）
      const currentMessageIds = new Set();
      
      // 状態からのメッセージID
      messages.forEach(msg => currentMessageIds.add(msg.id));
      
      // DOM内の動的要素からのメッセージID
      const dynamicElements = document.querySelectorAll('[id^="message_"]');
      dynamicElements.forEach(el => {
        const id = el.id.replace('message_', '');
        if (!isNaN(id)) {
          currentMessageIds.add(parseInt(id));
        }
      });
      
      // 実際に新しいメッセージのみを特定
      const newMessages = serverMessages.filter(msg => !currentMessageIds.has(msg.id));
      
      if (newMessages.length > 0) {
        // 状態に新しいメッセージを追加
        setMessages(prevMessages => {
          const existingIds = new Set(prevMessages.map(msg => msg.id));
          const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
          return [...prevMessages, ...uniqueNewMessages];
        });
        
        // 会話一覧の同期処理
        const latestMessage = newMessages[newMessages.length - 1];
        if (latestMessage) {
          // 他のユーザーからの新着メッセージ数を計算（型を統一して比較）
          const newMessagesFromOthers = newMessages.filter(msg => {
            const msgSenderId = parseInt(msg.sender.id);
            const currentUserId = parseInt(currentUser.id);
            const isFromOthers = msgSenderId !== currentUserId;
            
            console.log(`🔍 [LOAD NEW USER ID CHECK] Message ${msg.id}: sender=${msgSenderId}, current=${currentUserId}, fromOthers=${isFromOthers}`);
            return isFromOthers;
          });
          
          // 会話一覧の状態を更新
          setConversations(prevConversations => {
            return prevConversations.map(conv => {
              if (conv.id === conversationId) {
                return {
                  ...conv,
                  last_message: {
                    id: latestMessage.id,
                    text: latestMessage.text,
                    created_at: latestMessage.created_at,
                    sender_id: latestMessage.sender.id
                  },
                  updated_at: latestMessage.created_at,
                  // メッセージページを開いている間は、バッジのカウントをリセット（新着通知バーで表示するため）
                  unread_count: 0
                };
              }
              return conv;
            }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
          });
          
          // DOM要素も直接更新（即座に反映）
          const conversationElement = document.querySelector(`[data-conversation-id="${conversationId}"]`);
          if (conversationElement) {
            // 最新メッセージテキストを更新
            const lastMessageElement = conversationElement.querySelector('.messages__conversation-last-message');
            if (lastMessageElement) {
              lastMessageElement.textContent = latestMessage.text;
            }
            
            // メッセージページを開いている間は、バッジを非表示にする（新着通知バーで表示するため）
            const badgeElement = conversationElement.querySelector('.MuiBadge-root');
            if (badgeElement) {
              badgeElement.style.display = 'none';
            }
          }
        }
        
        // 通知バーを削除（全ての場所から）
        document.querySelectorAll('.messages__new-message-bar').forEach(bar => {
          bar.remove();
          console.log('🗑️ [LOAD NEW] Removed notification bar after loading messages');
        });
        
        // 新着メッセージを読み込んだ後はバッジをクリア
        const conversationElement = document.querySelector(`[data-conversation-id="${conversationId}"]`);
        if (conversationElement) {
          const badgeElement = conversationElement.querySelector('.MuiBadge-root');
          if (badgeElement) {
            badgeElement.style.display = 'none';
          }
        }
        
        // 会話一覧の状態でも未読カウントをクリア
        setConversations(prevConversations => {
          return prevConversations.map(conv => {
            if (conv.id === conversationId) {
              return { ...conv, unread_count: 0 };
            }
            return conv;
          });
        });
        
        // メッセージ数を更新
        lastMessageCountRef.current = serverMessages.length;
        
        // スクロール
        setTimeout(forceScrollToBottom, 100);
        
        console.log(`✅ [LOAD NEW] Loaded ${newMessages.length} new messages and cleared notification badge`);
      } else {
        // 新しいメッセージがない場合も通知バーを削除（全ての場所から）
        document.querySelectorAll('.messages__new-message-bar').forEach(bar => {
          bar.remove();
          console.log('🗑️ [LOAD NEW] Removed notification bar (no new messages)');
        });
        console.log('ℹ️ [LOAD NEW] No new messages to load');
      }
    } catch (error) {
      console.error('❌ [LOAD NEW] Error loading new messages:', error);
    }
  };

  if (loading) {
    return (
      <div className="messages">
      <div className="messages__loading">
        <CircularProgress />
          <p>読み込み中...</p>
      </div>
      </div>
    );
  }

  return (
    <div className="messages">
      {/* メイン部分 */}
      <div className={`messages__main ${isMobile && !showConversationList ? 'messages__main--mobile-full' : ''}`}>
        {/* サイドバー（会話一覧） - PC版では常に表示、モバイル版では条件付き表示 */}
        {(!isMobile || showConversationList) && (
      <div className="messages__sidebar">
        <div className="messages__header">
          <h2>メッセージ</h2>
          <IconButton onClick={handleNewConversation} color="primary">
            <AiOutlinePlus />
          </IconButton>
        </div>
        
            {error && <p className="messages__error">{error}</p>}
            
            <div className="messages__conversations">
              {conversations.map((conversation) => (
                    <div 
                      key={conversation.id} 
                      data-conversation-id={conversation.id}
                      className={`messages__conversation ${selectedConversation?.id === conversation.id ? 'messages__conversation--active' : ''}`}
                      onClick={() => selectConversation(conversation)}
                    >
                  <div className="messages__conversation-avatar">
                    {conversation.participants && conversation.participants.filter(p => p && typeof p === 'object').length > 0 ? (
                      (() => {
                        const otherP = conversation.participants.filter(p => p && typeof p === 'object' && p.id !== currentUser?.id);
                        return otherP.length > 0 ? (
                          <Avatar src={normalizeImageUrl(otherP[0].avatar)} />
                        ) : <Avatar />;
                      })()
                    ) : (
                      <Avatar />
                    )}
                        </div>
                  <div className="messages__conversation-content">
                    <div className="messages__conversation-header">
                      <h3>
                        {conversation.participants && conversation.participants.length > 0
                          ? conversation.participants.filter(p => p != null && typeof p === 'object' && 'id' in p && p.id !== currentUser?.id).map(p => p.display_name || p.username || '').join(', ') || '会話'
                          : '会話'}
                      </h3>
                      <span className="messages__conversation-time">
                        {formatTime(conversation.last_message?.created_at)}
                      </span>
                    </div>
                    <div className="messages__conversation-footer">
                      <p className="messages__conversation-last-message">
                        {(typeof conversation.last_message === 'string' ? conversation.last_message : conversation.last_message?.text) || 'メッセージがありません'}
                      </p>
                        {conversation.unread_count > 0 && (
                        <Badge badgeContent={conversation.unread_count} color="primary" />
                        )}
                      </div>
                  </div>
                      <IconButton 
                        size="small"
                    onClick={(e) => handleDeleteConversation(conversation.id, e)}
                    className="messages__delete-conversation"
                      >
                        <AiOutlineDelete />
                      </IconButton>
                    </div>
              ))}
          </div>
      </div>
      )}
      
        {/* メッセージ表示エリア - PC版では右カラム、モバイル版では全画面 */}
        <div className={`messages__content ${isMobile && showConversationList ? 'messages__content--hidden' : ''}`}>
          {/* スマホ表示でのヘッダー（戻るボタン付き） */}
          {isMobile && !showConversationList && (
            <div className="messages__mobile-header">
              <IconButton onClick={handleBackToConversationList}>
                <AiOutlineArrowLeft />
              </IconButton>
              <h3>
                {selectedConversation?.participants && selectedConversation.participants.length > 0
                      ? selectedConversation.participants.filter(p => p != null && typeof p === 'object' && 'id' in p && p.id !== currentUser?.id).map(p => p.display_name || p.username || '').join(', ') || '会話'
                  : creatingNew ? '新しいメッセージ' : '会話'}
              </h3>
            </div>
          )}

        {selectedConversation ? (
          <>
              {/* PC表示時のヘッダー */}
              {!isMobile && (
            <div className="messages__content-header">
              <h3>
                {selectedConversation.participants && selectedConversation.participants.length > 0
                  ? selectedConversation.participants.filter(p => p != null && typeof p === 'object' && 'id' in p && p.id !== currentUser?.id).map(p => p.display_name || p.username || '').join(', ') || '会話'
                  : '会話'}
              </h3>
            </div>
              )}
            
            <div className="messages__messages-container">
              <div className="messages__messages">
                {messagesLoading ? (
                  <div className="messages__loading">
                    <CircularProgress />
                    <p>メッセージを読み込み中...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="messages__empty">
                    <p>まだメッセージがありません</p>
                    <p>最初のメッセージを送信してみましょう！</p>
                  </div>
                ) : (
                  groupMessagesByDate(messages).map((item) => {
                  if (item.type === 'date-separator') {
                    return (
                      <div key={item.id} className="messages__date-separator">
                        <span className="messages__date-text">{item.date}</span>
                      </div>
                    );
                  }
                  
                  // メッセージの場合
                  const message = item;
                  const isFirstUnreadMessage = firstUnreadMessageId && message.id === firstUnreadMessageId;
                  
                                      return (
                      <Fragment key={message.id}>
                      {/* 未読メッセージマーカー */}
                      {isFirstUnreadMessage && (
                        <div className="messages__unread-marker">
                          <span className="messages__unread-text">ここから未読</span>
                        </div>
                      )}
                      
                      <div
                        data-message-id={message.id}
                        className={`messages__message ${(message.sender?.id || message.sender_id) === currentUser.id ? 'messages__message--own' : ''} ${message._isTemporary ? 'messages__message--pending' : ''} ${message._isDeleting ? 'messages__message--deleting' : ''}`}
                      >
                      {(message.sender?.id || message.sender_id) !== currentUser.id && (
                        <Avatar 
                          src={normalizeImageUrl(message.sender?.avatar)} 
                          className="messages__avatar" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/profile/${message.sender.username}`)}
                          title={`${message.sender.display_name || message.sender.username}のプロフィールを見る`}
                        />
                      )}
                      <div className={`messages__bubble ${message.is_deleted ? 'messages__bubble--deleted' : ''}`}>
                        {message.is_deleted ? (
                          <p style={{ color: '#999', fontStyle: 'italic' }}>このメッセージは削除されました</p>
                        ) : (
                          <p>{message.text}</p>
                        )}
                        <span className="messages__timestamp">
                          {message._isTemporary ? '送信中...' : 
                           message._isDeleting ? '削除中...' :
                           moment.utc(message.created_at).tz('Asia/Tokyo').format('HH:mm')}
                        </span>
                      </div>
                      
                      {/* 自分のメッセージかつ削除されていない場合のみ削除ボタンを表示 */}
                      {(message.sender?.id || message.sender_id) === currentUser.id && !message._isDeleting && !message.is_deleted && (
                        <IconButton 
                          size="small" 
                          className="messages__delete-button"
                          onClick={(e) => {
                              e.stopPropagation();
                            handleDeleteMessage(message.id);
                          }}
                          title="メッセージを削除"
                          disabled={message._isDeleting}
                        >
                          {message._isDeleting ? 
                            <CircularProgress size={16} color="inherit" /> : 
                            <AiOutlineDelete />
                          }
                        </IconButton>
                      )}
                      </div>
                    </Fragment>
                  );
                })
                )}
                <div ref={messagesEndRef} className="messages__end-anchor" />
              </div>
            </div>
            
            {/* 新着通知バー用のコンテナ（入力欄の上に配置） */}
            <div id="messages__dynamic-container" className="messages__dynamic-container"></div>
            
            <form className="messages__input" onSubmit={handleSendMessage}>
              <TextField
                fullWidth
                placeholder={isUserBlocked ? "このユーザーにメッセージを送ることはできません" : "メッセージを入力..."}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                variant="outlined"
                disabled={sendingMessage || isUserBlocked}
                InputProps={{
                  endAdornment: sendingMessage && (
                    <CircularProgress size={20} color="primary" />
                  ),
                  className: isUserBlocked ? "messages__input--blocked" : ""
                }}
              />
              <IconButton 
                type="submit" 
                color="primary" 
                disabled={!newMessage.trim() || sendingMessage || isUserBlocked}
              >
                {sendingMessage ? <CircularProgress size={24} color="inherit" /> : <AiOutlineSend />}
              </IconButton>
            </form>
          </>
        ) : creatingNew ? (
          <div className="messages__new-conversation">
              {/* PC表示時のヘッダー */}
              {!isMobile && (
            <div className="messages__content-header">
              <h3>新しいメッセージ</h3>
            </div>
              )}
            
            <div className="messages__search">
              <div className="messages__search-input">
                <AiOutlineSearch />
                <input
                  type="text"
                  placeholder="ユーザーを検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {selectedUsers.length > 0 && (
                <div className="messages__selected-users">
                  {selectedUsers.map(user => (
                    <div key={user.id} className="messages__selected-user">
                      <Avatar src={normalizeImageUrl(user.avatar)} sx={{ width: 24, height: 24 }} />
                      <span>{user.display_name || user.username}</span>
                      <button onClick={() => handleRemoveUser(user.id)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              
              {searchResults.length > 0 && (
                <div className="messages__search-results">
                  {searchResults.map(user => (
                    <div
                      key={user.id}
                      className="messages__search-result"
                      onClick={() => handleSelectUser(user)}
                    >
                      <Avatar src={normalizeImageUrl(user.avatar)} />
                      <div>
                        <h4>{user.display_name || user.username}</h4>
                        <p>@{user.username}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <form className="messages__input" onSubmit={(e) => { e.preventDefault(); handleCreateConversation(); }}>
              <TextField
                fullWidth
                placeholder={isUserBlocked ? "このユーザーにメッセージを送ることはできません" : "最初のメッセージを入力..."}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                variant="outlined"
                disabled={selectedUsers.length === 0 || searchLoading || isUserBlocked}
                InputProps={{
                  endAdornment: searchLoading && (
                    <CircularProgress size={20} color="primary" />
                  ),
                  className: isUserBlocked ? "messages__input--blocked" : ""
                }}
              />
              <IconButton 
                type="submit" 
                color="primary" 
                disabled={selectedUsers.length === 0 || !newMessage.trim() || searchLoading || isUserBlocked}
              >
                {searchLoading ? <CircularProgress size={24} color="inherit" /> : <AiOutlineSend />}
              </IconButton>
            </form>
          </div>
        ) : (
          <div className="messages__select-prompt">
            <div className="messages__prompt-container">
              <h3>会話を選択または新規作成してください</h3>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNewConversation}
                startIcon={<AiOutlinePlus />}
              >
                新しい会話を始める
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default Messages; 